/**
 * Teams of agents (ADR-0093): the steps a team is made of. Mirrors
 * platform/sdk/src/agent_platform_sdk/spec/team.py — the same team gives the same steps.
 *
 * A team's `spec.topology` (members, supervisor or handoff, a limit) is compiled into ordinary
 * graph steps, which the Builder draws, the data checks read and the orchestrator runs:
 * - supervisor: an AI step offered each member as a function (`agents`), one sub-agent step per
 *   member answering it, and a "Combine the answers" step after the limit of member calls;
 * - handoff: "Who is helping?" (the member who helped last keeps the conversation), "Choose who
 *   helps", one step per member, and "Hand over?" after a member that may hand over.
 */
import type { Text } from "../types";
import type { AgentSpec, SpecEdge, SpecNode } from "./types";

export type TeamKind = "supervisor" | "handoff";
/** Kinds older agent files may carry: `hierarchical` works as supervisor; `debate` no longer runs. */
export type StoredTeamKind = TeamKind | "debate" | "hierarchical";

export interface TeamMember {
  /** A registered agent: its name (the version released where the team runs) or name@version. */
  agent: string;
  /** Short name in the team, e.g. "Billing". */
  role: Text;
  /** What it is good at, in plain words (read by the supervisor to choose). */
  description?: Text | null;
  /** Handoff teams: the members this one may hand the conversation to. */
  can_handoff_to?: string[];
}

export interface Topology {
  kind: StoredTeamKind;
  members: TeamMember[];
  /** Supervisor: most member calls per request. Handoff: most hand-overs per request. */
  max_rounds?: number;
  judge?: string | null;
  /** Supervisor teams: ask every member at once (in parallel) and combine their answers. */
  together?: boolean;
}

export const TEAM_LIMIT_DEFAULT = 6;
export const TEAM_MAX_MEMBERS = 8;
export const SUPERVISOR = "supervisor";
export const COMBINE = "combine";
export const CHOOSE = "choose";
export const PICK = "pick";
export const HAND_OVER = "hand_over";
export const PASS_TO = "pass_to";
export const START = "start";
export const FINISH = "done";
export const NO_HANDOFF = "none";
const NO_CALL = "not exists(state.tool_call)";

export const memberName = (agent: string): string => agent.split("@", 1)[0] ?? agent;
export function slug(agent: string): string {
  return (
    memberName(agent)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 30) || "member"
  );
}
export const memberStep = (agent: string) => `member_${slug(agent)}`;
export const askFunction = (agent: string) => `ask_${slug(agent)}`;
export const memberAnswerKey = (agent: string) => `${slug(agent)}_answer`;

/** The team kind the runtime uses, or null when the team setting has no effect (debate). */
export function runningKind(t: Pick<Topology, "kind"> | null | undefined): TeamKind | null {
  if (!t) return null;
  if (t.kind === "handoff") return "handoff";
  if (t.kind === "supervisor" || t.kind === "hierarchical") return "supervisor";
  return null;
}

export function memberOf(t: Topology, agent: string): TeamMember | undefined {
  const name = memberName(agent);
  return t.members.find((m) => m.agent === agent || memberName(m.agent) === name);
}

const about = (m: TeamMember) => `- ${m.role.en} (${memberName(m.agent)}): ${m.description ? m.description.en : m.role.en}`;
const quoted = (v: string) => `"${v.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
const same = (ref: string, m: TeamMember) => ref === m.agent || memberName(ref) === memberName(m.agent);

export function supervisorPrompt(t: Topology): string {
  const members = t.members.map(about).join("\n");
  return (
    "You lead a team of agents. Ask the member whose work fits the request, or several " +
    "members in turn for its parts, by calling their functions with a clear request. " +
    "Then answer the person yourself, combining what the members said; say which member " +
    "a fact came from when it helps. Do not invent what a member did not say.\n\n" +
    `Team members:\n${members}\n\nRequest: {question}`
  );
}

export const COMBINE_PROMPT =
  "The team members were asked as many times as allowed. Using only what they answered " +
  "(above), write the final answer to the request. Say plainly what is still missing.\n\n" +
  "Request: {question}";

export function choosePrompt(t: Topology): string {
  const members = t.members.map(about).join("\n");
  return `Choose the team member who should help with this request, and say why in one short sentence.\n\nTeam members:\n${members}\n\nRequest: {question}`;
}

export function handOverPrompt(t: Topology): string {
  const members = t.members.map(about).join("\n");
  const allowed = t.members
    .filter((m) => (m.can_handoff_to ?? []).length)
    .map((m) => `- ${memberName(m.agent)} may hand over to: ${(m.can_handoff_to ?? []).map((r) => memberName(memberOf(t, r)?.agent ?? r)).join(", ")}`)
    .join("\n");
  return (
    "A team member just answered the request below. Hand the conversation to another " +
    "member only when the answer says it cannot help, or the request is about another " +
    `member's work. Otherwise answer '${NO_HANDOFF}'.\n\nTeam members:\n${members}\n\n` +
    `Who may hand over to whom:\n${allowed}\n\nMember who answered: {helping}\n` +
    "Request: {question}\nTheir answer: {answer}"
  );
}

const choiceShape = (options: string[]) => ({
  type: "object",
  properties: {
    to: { type: "string", enum: options, description: "The member who helps" },
    reason: { type: "string", description: "Why, in one short sentence" },
  },
  required: ["to", "reason"],
});

function memberNode(m: TeamMember, supervisor: boolean): SpecNode {
  return {
    id: memberStep(m.agent),
    label: m.role,
    ...(m.description ? { description: m.description } : {}),
    type: "subgraph",
    agent: m.agent,
    input_map: { question: supervisor ? "{{ state.tool_call.args.request }}" : "{{ state.question }}", lang: "{{ state.lang }}" },
    output_key: memberAnswerKey(m.agent),
  };
}

export interface TeamGraph {
  entry: string;
  nodes: SpecNode[];
  edges: SpecEdge[];
}

/** The steps of a team (supervisor or handoff; a stored `hierarchical` team is a supervisor). */
export function teamGraph(t: Topology, alias: string): TeamGraph {
  if (runningKind(t) === "handoff") return handoffGraph(t, alias);
  return t.together ? togetherGraph(t) : supervisorGraph(t);
}

/** Same words as the platform's `together_prompt` (sdk spec/team.py). */
export function togetherPrompt(t: Topology): string {
  const answers = t.members.map((m) => `- ${m.role.en}: {${memberAnswerKey(m.agent)}}`).join("\n");
  return (
    "Each team member below answered the request. Using only what they said, write one " +
    "short answer to the request. Say plainly when a member found nothing, and do not " +
    `invent anything they did not say.\n\n${answers}\n\nRequest: {question}`
  );
}

/** Every member at once (one parallel step), then one step combines their answers. */
function togetherGraph(t: Topology): TeamGraph {
  const steps = t.members.map((m) => memberStep(m.agent));
  const nodes: SpecNode[] = [{ id: "ask_all", type: "parallel", label: { en: "Ask every member at once", ar: "سؤال كل الأعضاء معاً" }, branches: steps, join: "answers" }];
  const edges: SpecEdge[] = [];
  for (const m of t.members) {
    nodes.push(memberNode(m, false));
    edges.push({ from: memberStep(m.agent), to: "answers" });
  }
  nodes.push({ id: "answers", type: "join", label: { en: "Their answers", ar: "إجاباتهم" }, strategy: "merge" });
  nodes.push({
    id: COMBINE,
    label: { en: "Combine the answers", ar: "جمع الإجابات" },
    type: "llm",
    prompt: { template: togetherPrompt(t), variables: [...t.members.map((m) => memberAnswerKey(m.agent)), "question"] },
  });
  nodes.push({ id: FINISH, type: "end" });
  edges.push({ from: "answers", to: COMBINE });
  edges.push({ from: COMBINE, to: FINISH });
  return { entry: "ask_all", nodes, edges };
}

function supervisorGraph(t: Topology): TeamGraph {
  const rounds = t.max_rounds ?? TEAM_LIMIT_DEFAULT;
  const nodes: SpecNode[] = [
    {
      id: SUPERVISOR,
      label: { en: "Supervisor", ar: "المشرف" },
      max_iterations: rounds,
      on_limit: COMBINE,
      description: { en: "Decides which member helps, asks them and combines their answers", ar: "يقرر أي عضو يساعد، ويسأله، ويجمع إجاباتهم" },
      type: "llm",
      prompt: { template: supervisorPrompt(t), variables: ["question"] },
      agents: t.members.map((m) => m.agent),
    },
  ];
  const edges: SpecEdge[] = [];
  for (const m of t.members) {
    nodes.push(memberNode(m, true));
    edges.push({ from: SUPERVISOR, to: memberStep(m.agent), when: `exists(state.tool_call) and state.tool_call.agent == ${quoted(m.agent)}` });
    edges.push({ from: memberStep(m.agent), to: SUPERVISOR });
  }
  nodes.push({
    id: COMBINE,
    label: { en: "Combine the answers", ar: "جمع الإجابات" },
    description: { en: "When the limit of member calls is reached: answers from what they said", ar: "عند بلوغ حدّ سؤال الأعضاء: يجيب مما قالوه" },
    type: "llm",
    prompt: { template: COMBINE_PROMPT, variables: ["question"] },
  });
  nodes.push({ id: FINISH, type: "end" });
  edges.push({ from: SUPERVISOR, to: FINISH, when: NO_CALL });
  edges.push({ from: COMBINE, to: FINISH });
  return { entry: SUPERVISOR, nodes, edges };
}

function handoffGraph(t: Topology, alias: string): TeamGraph {
  const refs = t.members.map((m) => m.agent);
  // While the team is being put together it may have no members yet: the steps still draw.
  const first = t.members[0] as TeamMember | undefined;
  const nodes: SpecNode[] = [
    {
      id: START,
      label: { en: "Who is helping?", ar: "من يساعد؟" },
      description: { en: "A conversation stays with the member who helped last", ar: "تبقى المحادثة مع العضو الذي ساعد آخر مرة" },
      type: "router",
      routes: t.members.map((m) => ({ when: `exists(state.previous.helping) and state.previous.helping == ${quoted(m.agent)}`, to: memberStep(m.agent) })),
      default: CHOOSE,
    },
    {
      id: CHOOSE,
      label: { en: "Choose who helps", ar: "اختيار من يساعد" },
      type: "llm",
      prompt: { template: choosePrompt(t), variables: ["question"] },
      model: { alias, output_schema: choiceShape(refs) },
      output_key: "handoff",
    },
    {
      id: PICK,
      label: { en: "To the chosen member", ar: "إلى العضو المختار" },
      type: "router",
      routes: t.members.slice(1).map((m) => ({ when: `state.handoff.to == ${quoted(m.agent)}`, to: memberStep(m.agent) })),
      default: first ? memberStep(first.agent) : FINISH,
    },
  ];
  const edges: SpecEdge[] = [{ from: CHOOSE, to: PICK }];
  const canHand = t.members.some((m) => (m.can_handoff_to ?? []).length);
  for (const m of t.members) {
    nodes.push(memberNode(m, false));
    edges.push({ from: memberStep(m.agent), to: (m.can_handoff_to ?? []).length ? HAND_OVER : FINISH });
  }
  if (canHand) {
    nodes.push({
      id: HAND_OVER,
      label: { en: "Hand over?", ar: "تسليم المحادثة؟" },
      max_iterations: t.max_rounds ?? TEAM_LIMIT_DEFAULT,
      on_limit: FINISH,
      description: { en: "Decides whether another member should take the conversation, and why", ar: "يقرر هل يتولى عضو آخر المحادثة، ولماذا" },
      type: "llm",
      prompt: { template: handOverPrompt(t), variables: ["question", "helping", "answer"] },
      model: { alias, output_schema: choiceShape([...refs, NO_HANDOFF]) },
      output_key: "handoff",
    });
    const routes: { when: string; to: string }[] = [];
    for (const target of t.members) {
      const senders = t.members.filter((m) => m.agent !== target.agent && (m.can_handoff_to ?? []).some((h) => same(h, target))).map((m) => m.agent);
      if (senders.length) routes.push({ when: `state.handoff.to == ${quoted(target.agent)} and state.helping in [${senders.map(quoted).join(", ")}]`, to: memberStep(target.agent) });
    }
    nodes.push({ id: PASS_TO, label: { en: "To the next member", ar: "إلى العضو التالي" }, type: "router", routes, default: FINISH });
    edges.push({ from: HAND_OVER, to: PASS_TO });
  }
  nodes.push({ id: FINISH, type: "end" });
  return { entry: START, nodes, edges };
}

/** The spec with its team (re)built: the steps follow the team; `topology: null` removes it. */
export function withTeam(spec: AgentSpec, topology: Topology | null): AgentSpec {
  const s = spec.spec;
  if (!topology) {
    const { topology: _t, ...rest } = s;
    return { ...spec, spec: rest as AgentSpec["spec"] };
  }
  const pattern = runningKind(topology) === "handoff" ? "handoff" : s.pattern === "hierarchical" ? "hierarchical" : "supervisor";
  return { ...spec, spec: { ...s, pattern, topology, graph: teamGraph(topology, s.model.alias) } };
}

/** Members the steps don't run (the steps were not rebuilt after the team changed). */
export function teamMismatch(spec: AgentSpec): string[] {
  const t = spec.spec.topology as Topology | undefined;
  const kind = runningKind(t);
  if (!t || !kind) return [];
  const nodes = spec.spec.graph.nodes;
  const run = new Set(nodes.filter((n) => n.type === "subgraph").map((n) => String(n.agent)));
  const asked = new Set(nodes.flatMap((n) => (n.type === "llm" ? ((n.agents as string[] | undefined) ?? []) : [])));
  const out = t.members.filter((m) => !run.has(m.agent)).map((m) => m.agent);
  // A supervisor asks its members as functions; a team that asks them all at once doesn't.
  if (kind === "supervisor" && !t.together) out.push(...t.members.filter((m) => !asked.has(m.agent) && run.has(m.agent)).map((m) => m.agent));
  return out;
}
